import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchPicker from "@/components/SearchPicker";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const num = (v) => Number(String(v).replace(",", "."));
const CURR = ["TRY", "USD", "EUR", "GBP"];
const INP = "bg-slate-950 border-slate-800 rounded-sm h-8 text-sm";

export const vLabel = (it) => {
  const l = (it.variant_label || "").trim();
  if (!l) return "";
  return /^\d+([.,]\d+)?$/.test(l) ? `No: ${l}` : l;
};

const sizeKey = (l) => { const n = parseFloat(String(l).replace(/^No:\s*/, "").replace(",", ".")); return Number.isFinite(n) ? n : Infinity; };
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

// ST içindeki tüm kalemler (aynı ürünün tüm bedenleri) için tek tedarikçi teklifi — mevcut POST /purchase-quotes REUSE, ortak batch_id
export default function BulkQuoteDialog({ request, onClose, onSaved }) {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [rows, setRows] = useState([]);
  const [gPrice, setGPrice] = useState("");
  const [gCurrency, setGCurrency] = useState("TRY");
  const [saving, setSaving] = useState(false);
  const [batchId, setBatchId] = useState("");
  const [saved, setSaved] = useState({});

  useEffect(() => {
    if (!request) return;
    setSupplierId(""); setGPrice(""); setGCurrency("TRY"); setBatchId(uid()); setSaved({});
    api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
    setRows((request.items || []).map((it, i) => ({
      key: `${it.material_id}::${it.variant_id || ""}::${i}`,
      material_id: it.material_id, variant_id: it.variant_id || "", kind: it.kind,
      name: it.name || "", code: it.code || "", label: vLabel(it), unit: it.unit || "adet",
      need: Number(it.quantity || 0), qty: String(it.quantity ?? ""), price: "", currency: "TRY",
    })).sort((a, b) => a.name.localeCompare(b.name, "tr") || sizeKey(a.label) - sizeKey(b.label) || a.label.localeCompare(b.label, "tr")));
  }, [request]);
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => { const x = (g[r.material_id] ||= { name: r.name, unit: r.unit, n: 0, qty: 0 }); x.n += 1; x.qty += num(r.qty) || 0; });
    return Object.values(g);
  }, [rows]);

  const setRow = (k, patch) => setRows((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));
  const applyAll = () => {
    if (!gPrice || !Number.isFinite(num(gPrice)) || num(gPrice) <= 0) { toast.error("Geçerli bir fiyat girin"); return; }
    setRows((rs) => rs.map((r) => ({ ...r, price: gPrice, currency: gCurrency })));
  };

  const valid = useMemo(() => rows.filter((r) => num(r.qty) > 0 && num(r.price) > 0), [rows]);
  const invalidRows = rows.filter((r) => !(num(r.qty) > 0 && num(r.price) > 0));

  const save = async () => {
    if (!supplierId) { toast.error("Tedarikçi seçin"); return; }
    if (!valid.length) { toast.error("Miktarı ve fiyatı girilen en az bir satır gerekli"); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    const done = { ...saved };
    for (const r of valid) {
      if (done[r.key]) continue;
      try {
        await api.post("/purchase-quotes", {
          request_id: request.id, material_id: r.material_id, variant_id: r.variant_id, kind: r.kind, batch_id: batchId,
          supplier_id: supplierId, quantity: num(r.qty), unit_price: num(r.price), currency: r.currency,
        });
        ok += 1; done[r.key] = true;
      } catch (e) { fail += 1; toast.error(`${r.code || r.name}${r.label ? ` · ${r.label}` : ""}: ${formatApiError(e)}`); }
    }
    setSaved(done);
    setSaving(false);
    if (ok) toast.success(`1 teklif kaydedildi (${ok} beden/varyant satırı)${fail ? ` — ${fail} satır başarısız, tekrar deneyebilirsiniz` : ""}`);
    if (ok) { onSaved(); if (!fail) onClose(); }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-3xl bg-slate-900 border-slate-800 p-4" data-testid="bulk-quote-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">
            Toplu Teklif Topla — <span className="font-mono text-amber-400">{request?.request_no}</span>
            <span className="text-slate-500 font-normal"> · {request?.source_sales_quote_no}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2" data-testid="bulk-quote-summary">
          {groups.map((g) => (
            <div key={g.name} className="text-xs border border-slate-800 rounded-sm px-2 py-1 bg-slate-950">
              <span className="font-semibold">{g.name}</span>
              <span className="text-slate-400 font-mono"> · {fmtQty(g.qty)} {g.unit} / {g.n} varyant</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-48">
            <label className="text-[11px] text-slate-400">Tedarikçi * (tüm satırlar için)</label>
            <SearchPicker
              testId="bulk-quote-supplier"
              items={suppliers}
              value={supplierId}
              onChange={(id) => setSupplierId(id || "")}
              getLabel={(s) => s.name || ""}
              placeholder="Tedarikçi ara…"
              minChars={0}
            />
          </div>
          <div className="w-28">
            <label className="text-[11px] text-slate-400">Birim fiyat (tümü)</label>
            <Input inputMode="decimal" value={gPrice} onChange={(e) => setGPrice(e.target.value)} className={INP} data-testid="bulk-quote-global-price" />
          </div>
          <div className="w-24">
            <label className="text-[11px] text-slate-400">Para birimi</label>
            <Select value={gCurrency} onValueChange={setGCurrency}>
              <SelectTrigger className={INP} data-testid="bulk-quote-global-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURR.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm h-8 text-[11px]" onClick={applyAll} data-testid="bulk-quote-apply-all">Tümüne Uygula</Button>
        </div>
        <div className="overflow-x-auto border border-slate-800 rounded-sm">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="border-b border-slate-800">
                {["Kalem", "İhtiyaç", "Teklif Miktarı", "Birim Fiyat", "Para Birimi"].map((h, i) => (
                  <th key={h} className={`text-[10px] uppercase tracking-widest text-slate-500 font-mono px-2 py-1.5 ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r) => {
                const bad = !(num(r.qty) > 0 && num(r.price) > 0);
                return (
                  <tr key={r.key} data-testid={`bulk-quote-row-${r.key}`} className={saved[r.key] ? "opacity-50" : ""}>
                    <td className="px-2 py-1.5 text-xs">
                      <span className="font-mono">{r.code || "—"}</span>
                      {r.label && <span className="text-sky-400"> · {r.label}</span>}
                      <span className="text-slate-500"> — {r.name}</span>
                    </td>
                    <td className="px-2 py-1.5 text-xs font-mono text-right text-slate-400 whitespace-nowrap">{fmtQty(r.need)} {r.unit}</td>
                    <td className="px-2 py-1.5 text-right">
                      <Input inputMode="decimal" value={r.qty} onChange={(e) => setRow(r.key, { qty: e.target.value })}
                        className={`${INP} h-7 w-24 text-xs text-right font-mono ml-auto`} data-testid={`bulk-quote-qty-${r.key}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Input inputMode="decimal" value={r.price} onChange={(e) => setRow(r.key, { price: e.target.value })}
                        className={`${INP} h-7 w-24 text-xs text-right font-mono ml-auto ${bad && r.price ? "border-rose-800" : ""}`} data-testid={`bulk-quote-price-${r.key}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Select value={r.currency} onValueChange={(v) => setRow(r.key, { currency: v })}>
                        <SelectTrigger className={`${INP} h-7 w-20 text-xs ml-auto`} data-testid={`bulk-quote-currency-${r.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{CURR.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
          <span data-testid="bulk-quote-valid-count">{valid.length}/{rows.length} satır kaydedilecek{invalidRows.length ? " (eksik miktar/fiyat satırları atlanır)" : ""}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={onClose} data-testid="bulk-quote-cancel">Vazgeç</Button>
            <Button size="sm" className="rounded-sm h-8 bg-emerald-600 hover:bg-emerald-500" disabled={saving || !supplierId || !valid.length} onClick={save} data-testid="bulk-quote-save">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Teklifleri Kaydet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
